import { defineStore } from "pinia";
import apiClient from "@/plugins/axios";
import { reactive, ref, computed } from "vue";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";

export const useLoginStore = defineStore("login", () => {
  const user = reactive(JSON.parse(localStorage.getItem("user")) || {});
  const company = ref(JSON.parse(localStorage.getItem("company") || "null"));
  const email = ref("");
  const password = ref("");
  const fieldErrors = ref({});
  const isSubmitting = ref(false);
  const isAuthenticated = ref(!!localStorage.getItem("auth_token"));
  const profileCount = ref(0);
  const router = useRouter();
  const toast = useToast();

  const resetLoginState = () => {
    email.value = "";
    password.value = "";
    fieldErrors.value = {};
  };

  // Computed: acceso completo si tiene datos empresariales
  const hasFullAccess = computed(() => !!company.value);

  const loginUser = async () => {
    isSubmitting.value = true;

    try {
      const response = await apiClient.post("/login", {
        email: email.value.toLowerCase(),
        password: password.value,
      });

      if (response.data.user) {
        Object.assign(user, response.data.user);
        localStorage.setItem("user", JSON.stringify(user));
      }

      if (response.data.company) {
        company.value = response.data.company;
        localStorage.setItem("company", JSON.stringify(company.value));
      } else {
        company.value = null;
        localStorage.removeItem("company");
      }

      localStorage.setItem("auth_token", response.data.token);
      isAuthenticated.value = true;
      fieldErrors.value = {};
      isSubmitting.value = false;
      return { success: true };
    } catch (error) {
      isSubmitting.value = false;
      fieldErrors.value = {};

      if (error.response) {
        const { status, data } = error.response;
        const { message, errors } = data;

        // Manejo explícito del 403
        if (status === 403) {
          fieldErrors.value.general = [message || "Acceso no autorizado"];
        } else if (errors && typeof errors === "object") {
          fieldErrors.value = errors;
        } else if (message) {
          fieldErrors.value.general = [message];
        } else {
          fieldErrors.value.general = ["Ocurrió un error inesperado"];
        }
      } else if (error.code === "ERR_NETWORK") {
        fieldErrors.value.general = [
          "No se pudo conectar al servidor. Intente más tarde.",
        ];
      } else {
        fieldErrors.value.general = [
          "Ocurrió un error inesperado. Intente nuevamente.",
        ];
      }

      return { success: false };
    }
  };

  const updateUser = (updatedUser) => {
    if (updatedUser) {
      Object.assign(user, updatedUser);
      localStorage.setItem("user", JSON.stringify(user));
    }
  };

  const updateSession = (token, user, company = null) => {
    if (token) {
      localStorage.setItem("auth_token", token);
    }
    if (user) updateUser(user);
    if (company !== undefined) updateCompany(company);
  };

  const updateCompany = (updatedCompany) => {
    if (updatedCompany) {
      company.value = updatedCompany;
      localStorage.setItem("company", JSON.stringify(company.value));
    } else {
      company.value = null;
      localStorage.removeItem("company");
    }
  };

  const fetchProfileCount = async () => {
    try {
      const response = await apiClient.get("/profile-count");
      profileCount.value = response.data.profile_count;
    } catch (error) {
      if (error.code === "ERR_NETWORK") {
        toast.error("No se pudo conectar al servidor.");
      } else {
        toast.error("Error al obtener los perfiles.");
      }
    }
  };

  const logoutUser = () => {
    // Limpiar localStorage
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    localStorage.removeItem("company");

    // Limpiar estados reactivos
    isAuthenticated.value = false;
    Object.assign(user, {});
    company.value = null;

    // Limpiar inputs y errores del login
    resetLoginState();

    // Redirigir al login
    router.push({ name: "login" });
  };

  return {
    // States
    email,
    password,
    user,
    company,
    fieldErrors,
    isSubmitting,
    isAuthenticated,
    profileCount,

    // Computed
    hasFullAccess,

    // Methods
    loginUser,
    logoutUser,
    updateCompany,
    fetchProfileCount,
    updateSession,
  };
});
